"use client";

import React from "react";
import { UserCheck, PiggyBank, Zap, Target, TrendingUp, Award, Check, X, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const comparisonPoints = [{
  id: 1,
  title: "100% Direct Senior Expertise",
  consultantText: "You work directly with me, Yassine. No client managers, junior handoffs, or trainees.",
  agencyText: "Pitched by senior directors, then quietly assigned to junior account executives.",
  badgeText: "Direct Access",
  iconName: "UserCheck"
}, {
  id: 2,
  title: "Cost-Efficient SEO Investment",
  consultantText: "100% of your budget goes into high-impact SEO execution and strategic campaign growth.",
  agencyText: "Retainers fund flashy offices, sales commissions, account managers, and agency overhead.",
  badgeText: "High ROI",
  iconName: "PiggyBank"
}, {
  id: 3,
  title: "Agile & Immediate Execution",
  consultantText: "I adapt strategies instantly and implement technical fixes without bureaucratic delays.",
  agencyText: "Changes require multi-layer approvals, ticketing queues, and slow monthly sign-offs.",
  badgeText: "Rapid Impact",
  iconName: "Zap"
}, {
  id: 4,
  title: "Bespoke Strategy Architecture",
  consultantText: "Every campaign is built from scratch around your specific market, tech stack, and goals.",
  agencyText: "Standardised assembly-line templates designed for high-volume client retention.",
  badgeText: "Bespoke SEO",
  iconName: "Target"
}, {
  id: 5,
  title: "Transparent, ROI-Focused Metrics",
  consultantText: "Clear reporting on genuine lead volume, keyword movement, and organic revenue growth.",
  agencyText: "Overcomplicated reports filled with vanity metrics to disguise lack of actual impact.",
  badgeText: "Honest Data",
  iconName: "TrendingUp"
}, {
  id: 6,
  title: "Personal Accountability",
  consultantText: "My personal reputation is tied to your growth. I am deeply invested in your SEO success.",
  agencyText: "You are one account out of dozens, managed by staff with high employee turnover.",
  badgeText: "Dedicated Partner",
  iconName: "Award"
}];
export default function Consultant() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  const getIcon = (name: string) => {
    switch (name) {
      case "UserCheck":
        return <UserCheck className="size-5 text-primary" />;
      case "PiggyBank":
        return <PiggyBank className="size-5 text-primary" />;
      case "Zap":
        return <Zap className="size-5 text-primary" />;
      case "Target":
        return <Target className="size-5 text-primary" />;
      case "TrendingUp":
        return <TrendingUp className="size-5 text-primary" />;
      case "Award":
        return <Award className="size-5 text-primary" />;
      default:
        return <ShieldCheck className="size-5 text-primary" />;
    }
  };
  return <section id="consultantvsagency" data-nav="light" className="bg-background text-foreground relative overflow-hidden py-20 lg:py-28">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="border-accent/30 bg-secondary text-accent-foreground mb-4 px-3 py-1 font-medium shadow-xs">
            <Sparkles className="size-3.5 text-accent" />
            <span>Independent Consultant vs SEO Agency</span>
          </Badge>
          <h2 className="font-serif text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Why Work Directly With Me?
          </h2>
          <p className="text-muted-foreground mt-4 text-base leading-relaxed md:text-lg">
            When you hire an SEO agency, you pay for sales teams, office overhead, and account managers. 
            When you work with me, Yassine Essaadi, you get direct senior expertise, agility, and full transparency.
          </p>
        </div>

        <div className="mt-12 rounded-2xl border border-border bg-card p-6 shadow-sm md:p-8 lg:p-10">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
            <div className="space-y-2 lg:max-w-2xl">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent">
                <ShieldCheck className="size-4" />
                <span>My Personal Guarantee</span>
              </div>
              <h3 className="font-serif text-xl font-semibold text-foreground md:text-2xl">
                {"\"You work directly with me — from audit to execution.\""}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
                I do not hide behind account managers or delegate your strategy to junior hires. I personally 
                analyze your site, engineer your technical roadmap, and optimize your content for long-term organic growth.
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactPage">
                  <span>Book a Free Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border">
                <Link to="/CaseStudiesPage">
                  <span>View Case Studies</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {comparisonPoints.map((point, i) => <motion.div key={point.id} variants={itemVariants} data-index={i}>
              <Card className="group border-border bg-card transition-[border-color,box-shadow] duration-200 hover:border-accent/40 hover:shadow-md">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="rounded-lg bg-primary/10 p-2.5">
                      {getIcon(point.iconName)}
                    </div>
                    <Badge variant="outline" className="border-accent/20 bg-secondary/50 text-accent font-medium text-xs">
                      {point.badgeText}
                    </Badge>
                  </div>
                  <CardTitle className="font-serif text-lg font-semibold text-foreground pt-3">
                    {point.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-lg bg-secondary/40 p-3.5 border border-accent/20">
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 rounded-full bg-accent/20 p-1 text-accent">
                        <Check className="size-3.5 stroke-[3]" />
                      </div>
                      <div>
                        <span className="block text-xs font-semibold text-accent uppercase tracking-wider">
                          Working With Me
                        </span>
                        <p className="mt-1 text-sm font-medium text-foreground leading-snug">
                          {point.consultantText}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg bg-muted/50 p-3.5 border border-border/60">
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 rounded-full bg-muted-foreground/20 p-1 text-muted-foreground">
                        <X className="size-3.5 stroke-[3]" />
                      </div>
                      <div>
                        <span className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Typical SEO Agency
                        </span>
                        <p className="mt-1 text-xs text-muted-foreground leading-snug">
                          {point.agencyText}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-16 rounded-2xl border border-border bg-gradient-to-r from-card via-secondary/30 to-card p-8 text-center shadow-xs md:p-12">
          <h3 className="font-serif text-2xl font-bold text-foreground sm:text-3xl">
            Ready to Cut Agency Overhead & Gain Direct Senior Expertise?
          </h3>
          <p className="text-muted-foreground mx-auto mt-3 max-w-2xl text-sm md:text-base leading-relaxed">
            Let us discuss your search performance and outline a clear, transparent SEO roadmap tailored 
            specifically to your market goals.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 px-8">
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border">
              <Link to="/ServicesPage">
                <span>Explore Services</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}